import { postedToPeriod } from './fixedAssetFormRules';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Link, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { FixedAssetService } from '../../services/fixedAsset.service';
import { FixedAssetDepreciationService } from '../../services/fixedAssetDepreciation.service';
import { FixedAsset } from '../../interfaces/fixedAsset/fixedAsset.interface';
import { DepreciationScheduleRow } from '../../interfaces/fixedAsset/fixedAssetReport.interface';
import { formatDate } from '../../utils/dateUtils';

const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AssetDepreciationSchedule: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [asset, setAsset] = useState<FixedAsset | null>(null);
  const [rows, setRows] = useState<DepreciationScheduleRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const [a, schedule] = await Promise.all([
          FixedAssetService.getById(id),
          FixedAssetDepreciationService.scheduleByAsset(id),
        ]);
        setAsset(a);
        setRows(schedule);
      } catch (e) {
        toast.showApiError(e, 'Failed to load depreciation schedule');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 900 }} data-help-page="fixedAssets.sched.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">
          Depreciation schedule{asset ? ` — ${asset.assetCode} ${asset.name}` : ''}
        </Typography>
        {asset && (
          <Button data-help="fixedAssets.sched.action.back" variant="outlined" onClick={() => navigate(`/fixed-assets/${asset.id}`)}>
            Back to asset
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" data-help-section="fixedAssets.sched.table">
          <TableHead>
            <TableRow>
              <TableCell data-help="fixedAssets.sched.col.period">Period</TableCell>
              <TableCell data-help="fixedAssets.sched.col.amount" align="right">Amount</TableCell>
              <TableCell data-help="fixedAssets.sched.col.book" align="right">Book value after</TableCell>
              <TableCell data-help="fixedAssets.sched.col.posted">Posted to</TableCell>
              <TableCell data-help="fixedAssets.sched.col.je">JE</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No depreciation posted yet.</Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((r, i) => (
              <TableRow key={`${r.period}-${i}`}>
                <TableCell>{formatDate(r.period)}</TableCell>
                <TableCell align="right">{money(r.amount)}</TableCell>
                <TableCell align="right">{money(r.bookValueAfter)}</TableCell>
                <TableCell>
                  {r.postedToPeriod
                    ? <Chip size="small" color="warning" label={formatDate(r.postedToPeriod)} />
                    : (postedToPeriod(r) ? formatDate(postedToPeriod(r)!) : '—')}
                </TableCell>
                <TableCell>
                  {r.journalEntryId
                    ? <Link data-help="fixedAssets.sched.action.je" component={RouterLink} to={`/accounting/journal-entries/${r.journalEntryId}`}>View JE</Link>
                    : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AssetDepreciationSchedule;
